import type { ReplicaResponse, ReplicaResponsePart } from '@unni-craftsman/turso-fts-common/dist/replication'
// INV-injected-replica-transport: cancellation reaches the physical callback; owner joins settlement before termination.
import type { ReplicaTransport } from './replica-transport'
import { nullTel, type Tel, type TransportBaggage, type BoundaryTelemetry } from '@w/tel'
import { serializeWorkerError, deserializeWorkerError, type WorkerCallError } from './worker-error'
type NextResult = ({ done: true } | { done: false; value: ReplicaResponsePart } | { error: WorkerCallError }) & { telemetry?: BoundaryTelemetry }
type Result = ({ bytes: Uint8Array } | { stream: true } | { error: WorkerCallError }) & { telemetry?: BoundaryTelemetry }
export type RemoteReplicaTransport = {
  request(id: string, operation: keyof ReplicaTransport, bytes: Uint8Array, baggage?: TransportBaggage): Promise<Result>
  next(id: string): Promise<NextResult>
  cancel(id: string): Promise<void> | void
}
export function createOwnedReplicaTransport(transport: ReplicaTransport, parent: Tel = nullTel) {
  // The existing app root owns reporting; a callback must never replace global telemetry.
  const owner = new AbortController()
  const calls = new Map<string, AbortController>()
  const streams = new Map<string, AsyncIterator<ReplicaResponsePart>>()
  const pending = new Set<Promise<unknown>>()
  const captures = new Map<string, Tel>()
  const remote: RemoteReplicaTransport = {
    request(id, operation, bytes, baggage) {
      const controller = new AbortController()
      calls.set(id, controller)
      const call = Promise.resolve().then(async (): Promise<Result> => {
        // Each callback owns its capture; overlapping attempts never share a mutable parent.
        let tel: Tel = nullTel
        if (baggage) {
          try { tel = parent._createTel({ ...baggage, name: 'db.transport.request', target: 'db', input: null }) }
          catch { /* Optional capture cannot prevent the physical callback. */ }
        }
        let result: Result
        try {
          owner.signal.throwIfAborted()
          const response = await transport[operation](bytes, AbortSignal.any([owner.signal, controller.signal]), tel)
          if (response instanceof Uint8Array) result = { bytes: response }
          else { streams.set(id, response[Symbol.asyncIterator]()); captures.set(id, tel); result = { stream: true } }
        } catch (error) { result = { error: serializeWorkerError(error) } }
        if (tel !== nullTel && !('stream' in result)) {
          // This span owns envelope delivery; the physical attempt owns failure reporting.
          try { tel.currentSpan.end({}); result.telemetry = tel._exportBoundary() }
          catch { /* Optional diagnostics cannot replace transport's decided outcome. */ }
        }
        return result
      })
        .finally(() => { if (!streams.has(id)) calls.delete(id); pending.delete(call) })
      pending.add(call)
      return call
    },
    next(id) {
      const call = (async (): Promise<NextResult> => {
        const iterator = streams.get(id)
        if (!iterator) return { error: serializeWorkerError(new Error('Unknown replica stream')) }
        let result: NextResult
        try {
          const item = await iterator.next()
          result = item.done ? { done: true } : { done: false, value: item.value }
        } catch (error) { result = { error: serializeWorkerError(error) } }
        if ('error' in result || result.done) {
          const tel = captures.get(id)
          try { tel?.currentSpan.end({}); result.telemetry = tel?._exportBoundary() } catch { /* Optional capture. */ }
          streams.delete(id); calls.delete(id); captures.delete(id)
        }
        return result
      })().finally(() => { pending.delete(call) })
      pending.add(call)
      return call
    },
    async cancel(id) {
      calls.get(id)?.abort()
      const iterator = streams.get(id)
      streams.delete(id); calls.delete(id)
      const call = Promise.resolve(iterator?.return?.()).finally(() => {
        const tel = captures.get(id)
        try { tel?.currentSpan.end({}) } catch { /* Optional capture. */ }
        captures.delete(id); pending.delete(call)
      })
      pending.add(call)
      await call
    },
  }
  return { remote, abort() { owner.abort() }, async join() { await Promise.all([...streams.keys()].map(id => Promise.resolve(remote.cancel(id)))); await Promise.all(pending) } }
}
export function receiveReplicaTransport(remote: RemoteReplicaTransport): ReplicaTransport {
  return { sendRemote: async (bytes, signal, tel) => { const result = await request('sendRemote', bytes, signal, tel); if (!(result instanceof Uint8Array)) throw new Error('Send requires a binary receipt'); return result }, fetchRemote: (bytes, signal, tel) => request('fetchRemote', bytes, signal, tel) }
  async function request(operation: keyof ReplicaTransport, bytes: Uint8Array, signal?: AbortSignal, parent: Tel = nullTel): Promise<ReplicaResponse> {
    signal?.throwIfAborted()
    const id = crypto.randomUUID()
    let tel: Tel = nullTel
    let baggage: TransportBaggage | undefined
    if (parent !== nullTel) {
      try {
        tel = parent._createTel({ name: 'db.transport.request', target: 'db', input: null })
        const { requestId: _requestId, ...transportBaggage } = tel._getBaggage()
        baggage = transportBaggage
      } catch { /* Optional capture cannot prevent the physical request. */ }
    }
    const pending = remote.request(id, operation, bytes, baggage)
    let cancellation: Promise<void> | undefined
    const cancel = () => { cancellation = Promise.resolve(remote.cancel(id)) }
    signal?.addEventListener('abort', cancel, { once: true })
    let streaming = false
    try {
      const result = await pending
      await cancellation
      if (result.telemetry) {
        try { tel._insertBoundaryTrace(result.telemetry.trace, result.telemetry.metrics, result.telemetry.resource) }
        catch { /* Preserve transport success/failure if diagnostics cannot be imported. */ }
      }
      if ('error' in result) throw deserializeWorkerError(result.error)
      signal?.throwIfAborted()
      if ('stream' in result) {
        streaming = true
        return { async *[Symbol.asyncIterator]() {
          try {
            for (;;) {
              signal?.throwIfAborted()
              const item = await remote.next(id)
              if (item.telemetry) tel._insertBoundaryTrace(item.telemetry.trace, item.telemetry.metrics, item.telemetry.resource)
              if ('error' in item) throw deserializeWorkerError(item.error)
              if (item.done) break
              yield item.value
            }
          } finally { signal?.removeEventListener('abort', cancel); await remote.cancel(id); tel.currentSpan.end({}) }
        } }
      }
      return result.bytes
    } finally {
      if (!streaming) signal?.removeEventListener('abort', cancel)
      try { if (!streaming) tel.currentSpan.end({}) } catch { /* Optional diagnostics. */ }
    }
  }
}
